import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Target, 
  Crosshair, 
  Package, 
  ShoppingCart, 
  BookOpen, 
  Boxes, 
  Activity, 
  Users, 
  MessageSquare, 
  UserCircle2, 
  Shield, 
  Home, 
  LineChart, 
  Menu, 
  X 
} from 'lucide-react';
import clsx from 'clsx';
import { conversationsAPI } from '../services/api';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  let isAdmin = false;
  try { isAdmin = JSON.parse(localStorage.getItem('authUser') || '{}')?.is_admin === true; } catch {}
  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Firearms', href: '/firearms', icon: Crosshair },
    { name: 'Components', href: '/components', icon: Package },
    { name: 'Purchases', href: '/purchases', icon: ShoppingCart },
    { name: 'Recipes', href: '/recipes', icon: BookOpen },
    { name: 'Loads', href: '/loads', icon: Boxes },
    { name: 'Firing Sessions', href: '/firing-sessions', icon: Activity },
    { name: 'Analytics', href: '/analytics', icon: LineChart },
    { name: 'Users', href: '/users', icon: Users },
    { name: 'Messages', href: '/messages', icon: MessageSquare },
    { name: 'Profile', href: '/profile', icon: UserCircle2 },
    ...(isAdmin ? [{ name: 'Admin', href: '/admin', icon: Shield }] : []),
  ];

  const handleLogout = () => {
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
    } catch {}
    window.location.href = '/login';
  };

  const [hasUnread, setHasUnread] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hasUnreadMessages') || 'false') === true; } catch { return false; }
  });

  useEffect(() => {
    const onUnreadChange = (e) => {
      if (e && typeof e.detail === 'boolean') {
        setHasUnread(e.detail);
      } else {
        try { setHasUnread(JSON.parse(localStorage.getItem('hasUnreadMessages') || 'false') === true); } catch { setHasUnread(false); }
      }
    };
    const onStorage = (e) => {
      if (e.key === 'hasUnreadMessages') {
        try { setHasUnread(JSON.parse(e.newValue || 'false') === true); } catch { setHasUnread(false); }
      }
    };
    // Poll unread status periodically so badge updates regardless of current page
    let timerId;
    const pollUnread = async () => {
      try {
        // Skip polling if not authenticated
        const token = localStorage.getItem('authToken');
        if (!token) return;
        try {
          const convRes = await conversationsAPI.listWithMessages();
          const me = JSON.parse(localStorage.getItem('authUser') || '{}');
          const myId = me?.id;
          const has = (Array.isArray(convRes.data) ? convRes.data : []).some(cv => (cv.messages || []).some(msg => (msg.sender_user_id !== myId) && (typeof msg.read_by_me === 'boolean' ? !msg.read_by_me : !msg.read_at)));
          setHasUnread(has);
          try {
            localStorage.setItem('hasUnreadMessages', JSON.stringify(has));
            window.dispatchEvent(new CustomEvent('unreadMessagesChange', { detail: has }));
          } catch {}
          return;
        } catch {}
        const has = false;
        setHasUnread(has);
        try {
          localStorage.setItem('hasUnreadMessages', JSON.stringify(has));
          window.dispatchEvent(new CustomEvent('unreadMessagesChange', { detail: has }));
        } catch {}
      } catch {
        // ignore polling errors
      }
    };
    // immediate and interval
    pollUnread();
    timerId = setInterval(pollUnread, 20000);
    const onVisibility = () => { if (document.visibilityState === 'visible') pollUnread(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('unreadMessagesChange', onUnreadChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('unreadMessagesChange', onUnreadChange);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
      if (timerId) clearInterval(timerId);
    };
  }, []);

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <Target className="h-8 w-8 text-primary-600" />
              <span className="text-xl font-bold text-gray-900">Reloading Tracker</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              const isMessages = item.name === 'Messages';
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    'flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  )}
                >
                  <div className="relative flex items-center">
                    <Icon className="h-4 w-4" />
                    {isMessages && hasUnread && (
                      <span className="absolute -top-2 -right-2 h-2 w-2 bg-red-600 rounded-full" aria-label="Unread messages" />
                    )}
                  </div>
                  <span>{item.name}</span>
                </Link>
              );
            })}
            <button onClick={handleLogout} className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">Logout</button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 hover:text-gray-900 focus:outline-none focus:text-gray-900 p-2"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              const isMessages = item.name === 'Messages';
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={clsx(
                    'flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium transition-colors',
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  )}
                >
                  <div className="relative flex items-center">
                    <Icon className="h-5 w-5" />
                    {isMessages && hasUnread && (
                      <span className="absolute -top-2 -right-2 h-2 w-2 bg-red-600 rounded-full" aria-label="Unread messages" />
                    )}
                  </div>
                  <span>{item.name}</span>
                </Link>
              );
            })}
            <button onClick={handleLogout} className="w-full text-left text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-base font-medium">Logout</button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;